import _ from 'lodash';
import { View } from 'backbone.marionette';
import { FantasyTeam } from '../../../../../models';
import Radio from 'backbone.radio';
import { Preloader, isMobileView, isMobile } from '../../../../../utils';
import Backbone from "backbone";

const
	silent = { silent: true },
	fieldChannel = Radio.channel('field');

export class TeamFieldCore extends View {
	initialize() {
		this.model = new FantasyTeam({
			positions_by_id: {},
			swap_player_id: false,
			filled: {},
			is_coach_enabled: false,
			actual_round_id: '',
			last_round_id: 1,
			selected_stat: 'cost'
		});

		this.listenTo(this.model, 'change:lineup', this.render);
		this.listenTo(this.model, 'change:selected_stat', this.render);
		this.listenTo(fieldChannel, 'change:stat:field', this.updateStatField);
	}

	ui() {
		return {
			more_tools: '.js-more-tools',
			player_cell: '.player-cell',
			remove: '.js-remove',
			favourite: '.js-favourite',
			substitute: '.js-substitute, .is-accepted-for-swap',
			substitute_up: '.js-substitute-up',
			substitute_down: '.js-substitute-down',
			cancel_substitute: '.js-cancel-substitute',
			set_captain: '.js-set-captain',
			set_vice_captain: '.js-set-vice-captain',
			btn_filter_by_position: '.player-cell-empty',
			btn_coach: '.js-coach-locked',
			btn_trade: '.js-trade'
		}
	}

	events() {
		return {
			'click @ui.set_captain': 'onSetCaptain',
			'click @ui.set_vice_captain': 'onSetViceCaptain',
			'click @ui.more_tools': 'toggleMoreTools',
			'click @ui.player_cell': 'onPlayerCellClick',
			'click @ui.remove': 'onRemovePlayer',
			'click @ui.favourite': 'onFavourite',
			'click @ui.substitute': 'onSubstitute',
			'click @ui.btn_filter_by_position': 'onSwitchFilterByPosition',
			'click @ui.btn_coach': 'showCoachPopup',
			'click @ui.btn_trade': 'onTradePlayer',
			'click @ui.substitute_up': 'onSubstituteUp',
			'click @ui.substitute_down': 'onSubstituteDown',
		}
	}

	triggers() {
		return {
			'click @ui.cancel_substitute': 'player:substitute:cancel',
		}
	}

	getUIElements(event) {
		event.preventDefault();

		const
			player_cell_class_name = '.player-cell',
			target = event.currentTarget,
			$target = $(target),
			$players = $(player_cell_class_name),
			$parent_tmp = $target.closest(player_cell_class_name),
			$parent =  $parent_tmp.size() ? $parent_tmp : $target;

		return {
			$target,
			$parent,
			$players,
			target,
			parent: $parent.get(0)
		};
	}

	updateStatField(selected_stat){
		this.model.set({ selected_stat });
	}

	showCoachPopup(event) {
		event.stopPropagation();
		Radio.channel('coach').trigger('show:popup');
	}

	onRemovePlayer(event) {
		event.stopPropagation();

		if (isMobileView()) {
			Preloader.showImmediately();
		}

		const
			{ parent } = this.getUIElements(event),
			{ playerId: id } = parent.dataset;

		this.trigger('player:remove', {
			id: parseInt(id, 10),
		});
	}

	toggleMoreTools(event) {
		event.stopPropagation();

		const { $parent, $players } = this.getUIElements(event),
			toggle_class_name = 'is-more-tools-opened',
			was_opened = $parent.hasClass(toggle_class_name);

		if ($parent.hasClass('is-accepted-for-swap')) {
			return false;
		}

		if ($parent.hasClass('is-not-accepted-for-swap')) {
			return false;
		}

		$players.removeClass(toggle_class_name);

		if (!was_opened) {
			$parent.addClass(toggle_class_name);
		}
	}

	onPlayerCellClick(event) {
		const
			{ $parent, parent } = this.getUIElements(event),
			{ playerId: id } = parent.dataset,
			is_in_swap_mode = (
				$parent.hasClass('is-accepted-for-swap') ||
				$parent.hasClass('is-not-accepted-for-swap')
			);

		if (is_in_swap_mode) {
			return this;
		}

		this.trigger('player:show:popup', {
			id: parseInt(id, 10),
		});

		return this;
	}

	onFavourite(event) {
		event.stopPropagation();

		const
			{ $target, parent } = this.getUIElements(event),
			{ playerId: id } = parent.dataset;

		$target.toggleClass('is-favourite');

		this.trigger('player:favourite', {
			id: parseInt(id, 10),
		});
	}

	getPlayerCreds(event){
		const
			{ parent } = this.getUIElements(event),
			{ playerId: id, positionId: pos } = parent.dataset;

		return { pos, id: parseInt(id, 10) };
	}

	onSubstitute(event) {
		event.stopPropagation();
		this.trigger('player:substitute', this.getPlayerCreds(event));
	}

	onSubstituteUp(event){
		event.stopPropagation();
		this.trigger('player:substitute:up', this.getPlayerCreds(event));
	}

	onSubstituteDown(event){
		event.stopPropagation();
		this.trigger('player:substitute:down', this.getPlayerCreds(event));
	}

	onSwitchFilterByPosition(event) {
		const { positionId: pos } = event.currentTarget.dataset;

		this.trigger('switch:position', { pos });
	}

	triggerLeaderChange(event, leader_name) {
		event.stopPropagation();

		const
			{ parent } = this.getUIElements(event),
			{ playerId } = parent.dataset,
			id = parseInt(playerId, 10);

		if (this.model.get(leader_name) === id) {
			return false;
		}

		this.trigger(`player:set:${leader_name}`, { id });

		return this;
	}

	onSetCaptain(event) {
		this.triggerLeaderChange(event, 'captain');
	}

	onSetViceCaptain(event) {
		this.triggerLeaderChange(event, 'vice_captain');
	}

	init({ team, ...args }) {
		const
			{
				positions_by_id,
				is_unlimited_trades,
				is_mobile_view,
				players_by_id,
				substitute,
				quick_trade,
				is_coach_enabled,
				has_assistant_coach,
				actual_round_id,
				last_round_id,
				selected_stat
			} = args,

			params = {
				...team,
				positions_by_id,
				is_unlimited_trades,
				is_mobile_view,
				is_coach_enabled,
				has_assistant_coach,
				actual_round_id,
				last_round_id,
				selected_stat
			};

		this.model
			.set(params, silent)
			.trigger('change:team')
			.set({ filled: this.model.getFilledInfo() }, silent)
			.transformPlayersIdToObjects({
				players_by_id,
				substitute_player: _.first(substitute),
				trade_out_player: _.first(quick_trade),
			})
			.trigger('change:lineup');
	}

	onTradePlayer(event){
		event.stopPropagation();

		const
			{ parent } = this.getUIElements(event),
			{ playerId: id, positionId: pos } = parent.dataset;

		if (!isMobile() && !isMobileView()) {
			return Backbone.history.navigate(`/fantasy/trades/${id}`, { trigger: true });
		}

		this.trigger('player:trade:out', {
			pos: pos === 'bench' ? pos : parseInt(pos, 10),
			id: parseInt(id, 10),
		});
	}
}

export default TeamFieldCore;